/**
 * Location Service
 * 
 * Service for handling location-related API calls.
 */

import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";

export interface Location {
  provinsi: string;
  kabupaten: string;
}

export interface LocationResponse {
  locations: Location[];
}

export interface ProvinceResponse {
  provinces: string[];
}

export interface CitiesResponse {
  cities: string[];
}

class LocationService {
  private cachedLocations: Location[] | null = null;
  private cachedProvinces: string[] | null = null;
  private cachedCitiesByProvince = new Map<string, string[]>();
  private cachedKecamatanByCity = new Map<string, string[]>();
  private cachedRWByKecamatan = new Map<string, string[]>();
  private cachedRTByRW = new Map<string, string[]>();
  private controllerMap = new Map<string, AbortController>();
  private debounceTimers = new Map<string, NodeJS.Timeout>();

  private getCacheKey(type: string, ...params: string[]): string {
    return `${type}:${params.join(':')}`;
  }

  private clearCacheEntry(key: string) {
    const controller = this.controllerMap.get(key);
    if (controller) {
      controller.abort();
      this.controllerMap.delete(key);
    }
    const timer = this.debounceTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      this.debounceTimers.delete(key);
    }
  }

  /**
   * Fetch all unique locations (provinsi + kabupaten combinations)
   * @returns Promise with array of unique locations
   */
  async fetchLocations(): Promise<Location[]> {
    // Return cached data if available
    if (this.cachedLocations) {
      return this.cachedLocations;
    }

    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/locations`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: LocationResponse = await response.json();
      
      // Cache the locations
      this.cachedLocations = data.locations;
      
      return data.locations;
    } catch (error) {
      console.error("Error fetching locations:", error);
      throw error;
    }
  }

  /**
   * Fetch all unique provinces
   * @returns Promise with array of unique province names
   */
  async fetchProvinces(): Promise<string[]> {
    // Return cached data if available
    if (this.cachedProvinces !== null) {
      return this.cachedProvinces;
    }

    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/locations/provinces`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: ProvinceResponse = await response.json();
      
      // Cache the provinces
      this.cachedProvinces = data.provinces;
      
      return data.provinces;
    } catch (error) {
      console.error("Error fetching provinces:", error);
      throw error;
    }
  }

  /**
   * Fetch cities for a specific province
   * @param province - Province name to fetch cities for
   * @returns Promise with array of unique city names
   */
  async fetchCitiesByProvince(province: string): Promise<string[]> {
    // Return cached data if available
    if (this.cachedCitiesByProvince.has(province)) {
      return this.cachedCitiesByProvince.get(province)!;
    }

    try {
      const params = new URLSearchParams({ province });
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/locations/cities?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: CitiesResponse = await response.json();
      
      // Cache the cities for this province
      this.cachedCitiesByProvince.set(province, data.cities);
      
      return data.cities;
    } catch (error) {
      console.error("Error fetching cities by province:", error);
      throw error;
    }
  }

  /**
   * Fetch kecamatan for a specific city
   * @param city - City name to fetch kecamatan for
   * @param province - Province name
   * @returns Promise with array of kecamatan names
   */
  async fetchKecamatanByCity(city: string, province: string): Promise<string[]> {
    const cacheKey = `${province}|${city}`;

    // Return cached data if available
    if (this.cachedKecamatanByCity.has(cacheKey)) {
      return this.cachedKecamatanByCity.get(cacheKey)!;
    }

    try {
      const params = new URLSearchParams({ city, province });
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/locations/kecamatan?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Cache the kecamatan for this city
      this.cachedKecamatanByCity.set(cacheKey, data.kecamatan);

      return data.kecamatan;
    } catch (error) {
      console.error("Error fetching kecamatan by city:", error);
      throw error;
    }
  }

  /**
   * Fetch RW for a specific kecamatan
   * @param kecamatan - Kecamatan name
   * @param city - City name
   * @param province - Province name
   * @returns Promise with array of RW names
   */
  async fetchRWByKecamatan(kecamatan: string, city: string, province: string): Promise<string[]> {
    const cacheKey = `${province}|${city}|${kecamatan}`;

    // Return cached data if available
    if (this.cachedRWByKecamatan.has(cacheKey)) {
      return this.cachedRWByKecamatan.get(cacheKey)!;
    }

    try {
      const params = new URLSearchParams({ kecamatan, city, province });
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/locations/rw?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Cache the RW for this kecamatan
      this.cachedRWByKecamatan.set(cacheKey, data.rw);

      return data.rw;
    } catch (error) {
      console.error("Error fetching RW by kecamatan:", error);
      throw error;
    }
  }

  /**
   * Fetch RT for a specific RW
   * @param rw - RW name
   * @param kecamatan - Kecamatan name
   * @param city - City name
   * @param province - Province name
   * @returns Promise with array of RT names
   */
  async fetchRTByRW(rw: string, kecamatan: string, city: string, province: string): Promise<string[]> {
    const cacheKey = `${province}|${city}|${kecamatan}|${rw}`;

    // Return cached data if available
    if (this.cachedRTByRW.has(cacheKey)) {
      return this.cachedRTByRW.get(cacheKey)!;
    }

    try {
      const params = new URLSearchParams({ rw, kecamatan, city, province });
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/locations/rt?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Cache the RT for this RW
      this.cachedRTByRW.set(cacheKey, data.rt);

      return data.rt;
    } catch (error) {
      console.error("Error fetching RT by RW:", error);
      throw error;
    }
  }

  /**
   * Clear cached location data
   */
  clearCache(): void {
    this.cachedLocations = null;
    this.cachedProvinces = null;
    this.cachedCitiesByProvince.clear();
    this.cachedKecamatanByCity.clear();
    this.cachedRWByKecamatan.clear();
    this.cachedRTByRW.clear();
  }
}

export const locationService = new LocationService();

